import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { PoModalAction, PoModalComponent } from '@po-ui/ng-components';

import { resourceTypeLabel } from '../../../core/utils/resource-type-label.util';
import {
  ReviewStatsAttentionItem, ReviewStatsPeriodFilters
} from '../../interfaces/review-stats.interface';
import { ReviewStatsService } from '../../review-stats.service';

type TagType = 'danger' | 'warning' | 'success' | 'neutral';

interface RecentReview {
  title: string;
  subtitle: string;
  high: string;
  verdict: string;
  verdictType: TagType;
  resourceType: string;
  resourceId: string;
}

const VERDICTS: Record<string, { label: string; type: TagType }> = {
  REJEITADO: { label: 'Rejeitado', type: 'danger' },
  AJUSTES_NECESSARIOS: { label: 'Ajustes necessários', type: 'warning' },
  APROVADO: { label: 'Aprovado', type: 'success' },
  INSUFICIENTE: { label: 'Resposta inválida', type: 'neutral' }
};

/** Quantos reviews recentes o resumo lista antes do "Ver todos". */
const RECENT_LIMIT = 5;

/**
 * "Reviews que pedem ação" em versão resumida: o total, os dois motivos e os mais
 * recentes. A grade completa abre num modal largo — dentro do card ela cortava as
 * colunas da direita — e só é montada, e só busca, quando o modal abre.
 */
@Component({
  selector: 'app-review-stats-attention-summary',
  templateUrl: './review-stats-attention-summary.component.html',
  styleUrls: ['./review-stats-attention-summary.component.css']
})
export class ReviewStatsAttentionSummaryComponent implements OnChanges {
  @Input() filters: ReviewStatsPeriodFilters = {};
  @ViewChild(PoModalComponent, { static: true }) modal?: PoModalComponent;

  /** Monta a grade só enquanto o modal está aberto. */
  expanded = false;
  readonly closeAction: PoModalAction = { label: 'Fechar', action: () => this.collapse() };
  total = 0;
  withHigh = 0;
  withRejected = 0;
  undetailed = 0;
  recent: RecentReview[] = [];
  loading = false;
  loadError = false;

  private requestSequence = 0;

  constructor(private statsService: ReviewStatsService, private router: Router) {}

  ngOnChanges(): void {
    this.loading = true;
    this.loadError = false;
    const request = ++this.requestSequence;
    this.statsService.getAttention({ ...this.filters, page: 1, pageSize: RECENT_LIMIT }).subscribe({
      next: page => {
        if (request !== this.requestSequence) { return; }
        this.total = page.total;
        this.withHigh = page.withHigh ?? 0;
        this.withRejected = page.withRejected ?? 0;
        this.undetailed = page.undetailed;
        this.recent = page.items.map(item => this.toRecent(item));
        this.loading = false;
      },
      error: () => {
        if (request !== this.requestSequence) { return; }
        this.loading = false;
        this.loadError = true;
      }
    });
  }

  get totalLabel(): string {
    return this.total.toLocaleString('pt-BR');
  }

  get highLabel(): string {
    return `${this.withHigh.toLocaleString('pt-BR')} com ação Alta`;
  }

  get rejectedLabel(): string {
    return `${this.withRejected.toLocaleString('pt-BR')} com arquivo rejeitado`;
  }

  get undetailedLabel(): string {
    if (!this.undetailed) { return ''; }
    return this.undetailed === 1
      ? '1 review ainda sem severidade detalhada ficou de fora.'
      : `${this.undetailed.toLocaleString('pt-BR')} reviews ainda sem severidade detalhada ficaram de fora.`;
  }

  /** A grade traz o que o resumo não traz — todas as severidades e categorias —, então o
   *  botão aparece mesmo quando os recentes já são todos. */
  get showAllLabel(): string {
    return this.total > this.recent.length ? `Ver todos os ${this.totalLabel} reviews` : 'Ver detalhes';
  }


  expand(): void {
    this.expanded = true;
    this.modal?.open();
  }

  collapse(): void {
    this.modal?.close();
    this.expanded = false;
  }

  /** A grade vai navegar para um review: o modal não pode ficar aberto por cima dele. */
  onReviewOpened(): void {
    this.collapse();
  }

  open(review: RecentReview): void {
    this.router.navigate(['/menu/smart-review/reviews', review.resourceType, review.resourceId]);
  }

  private toRecent(item: ReviewStatsAttentionItem): RecentReview {
    const verdict = VERDICTS[item.worstVerdict ?? ''];
    return {
      title: item.label || item.resourceId,
      subtitle: [resourceTypeLabel(item.resourceType) || item.resourceType, formatDateTime(item.requestedAt)]
        .filter(Boolean).join(' · '),
      high: item.severityHigh ? `${item.severityHigh} Alta` : '',
      verdict: verdict?.label ?? '',
      verdictType: verdict?.type ?? 'neutral',
      resourceType: item.resourceType,
      resourceId: item.resourceId
    };
  }
}

/** RFC 3339 em UTC → data e hora locais, `dd/mm/aaaa hh:mm`. */
function formatDateTime(raw: string): string {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) { return raw || ''; }
  return date.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}
